/**
 * The Pick history card's logic (owner 2026-10-08; design handoff `design_handoff_pick_history`, README §1 and §4).
 * Pure, so vitest pins every rule and label; `PickHistoryCard` only draws what this returns.
 *
 * Search and the kind filter work on the rows already loaded, with no server call. The rows are the pick ledger's,
 * so an item's entries always add up to its Picked figure in the Items table.
 */
import { phtFullDate, phtMonthDay, phtTimeOfDay, phtTimeWithSeconds } from '@/features/activity/lib/phtDay';
import type { PickHistoryDto, PickHistoryEntryDto, PickHistoryGroupDto } from '../types/pickHistory.types';

export type PickHistoryKindFilter = 'all' | 'picks' | 'adjustments';
export type PickHistoryLayout = 'wide' | 'mid' | 'narrow';

/** The card's width decides the columns: nothing ever scrolls sideways, the columns reflow instead. */
export function layoutFor(cardWidth: number): PickHistoryLayout {
  if (cardWidth >= 880) return 'wide';
  if (cardWidth >= 560) return 'mid';
  return 'narrow';
}

/** A Submit with more rows than this, and no search, shows only its latest `CAP_SHOW` until opened. */
export const CAP_OVER = 15;
export const CAP_SHOW = 10;

/** A string split around its first case-insensitive match, so the match can be marked. */
export interface Marked {
  a: string;
  b: string;
  c: string;
}

export function mark(text: string, q: string): Marked {
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  return at < 0 ? { a: text, b: '', c: '' } : { a: text.slice(0, at), b: text.slice(at, at + q.length), c: text.slice(at + q.length) };
}

/** Search fields: serial, item code, item name and MRS number, case-insensitive substring. */
export function entryMatches(e: PickHistoryEntryDto, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return [e.serial, e.itemCode, e.itemName, e.mrsNumber].some((v) => (v ?? '').toLowerCase().includes(needle));
}

function qty(n: number): string {
  return Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 4 });
}

/** `+1 SET` / `−1 SET` (U+2212). */
export function signedQuantity(n: number, unit: string): string {
  return `${n < 0 ? '−' : '+'}${qty(n)}${unit ? ` ${unit}` : ''}`;
}

function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

/** Damaged and Missing are written off: NetSuite still counts the unit until someone adjusts it there. */
const WRITE_OFFS = new Set(['damaged', 'missing']);

export interface PickHistoryRowView {
  id: number;
  date: string;
  time: string;
  timeTitle: string;
  code: Marked;
  name: Marked;
  itemTitle: string;
  mrs: Marked;
  materialRequestId: number;
  mrsNumber: string;
  mrsSub: string | null;
  bin: string;
  serial: Marked | null;
  serialFull: string | null;
  qty: string;
  isReversal: boolean;
  disposition: string | null;
  /** True for Damaged / Missing: the warning badge with its NetSuite tooltip. */
  dispositionWarn: boolean;
  dispositionTip: string | null;
  reading: string | null;
}

export interface PickHistoryGroupView {
  id: string;
  kind: PickHistoryGroupDto['kind'];
  label: 'Picked' | 'Adjusted';
  meta: string;
  notes: string | null;
  rows: PickHistoryRowView[];
  /** "Showing the latest 10 of 95 picks in this submit", when capped or opened; null otherwise. */
  moreText: string | null;
  moreLabel: string | null;
  expanded: boolean;
}

export interface PickHistoryView {
  groups: PickHistoryGroupView[];
  counts: Record<PickHistoryKindFilter, number>;
  /** Rows drawn, after search, filter and the cap. */
  shown: number;
  total: number;
  noMatch: boolean;
  noMatchText: string;
  clearLabel: string;
}

function rowView(e: PickHistoryEntryDto, q: string): PickHistoryRowView {
  const dispositionWarn = WRITE_OFFS.has((e.disposition ?? '').toLowerCase());
  return {
    id: e.id,
    date: phtFullDate(e.at),
    time: phtTimeWithSeconds(e.at),
    timeTitle: `${phtFullDate(e.at)}, ${phtTimeWithSeconds(e.at)} PHT`,
    code: mark(e.itemCode, q),
    name: mark(e.itemName, q),
    itemTitle: e.itemName ? `${e.itemCode} · ${e.itemName}` : e.itemCode,
    mrs: mark(e.mrsNumber, q),
    materialRequestId: e.materialRequestId,
    mrsNumber: e.mrsNumber,
    mrsSub: e.mrsLineSub,
    bin: e.binCode,
    serial: e.serial ? mark(e.serial, q) : null,
    serialFull: e.serial,
    qty: signedQuantity(e.quantity, e.unit),
    isReversal: e.quantity < 0,
    disposition: e.quantity < 0 ? e.disposition : null,
    dispositionWarn: e.quantity < 0 && dispositionWarn,
    dispositionTip:
      e.quantity < 0 && dispositionWarn
        ? `Recorded as ${(e.disposition ?? '').toLowerCase()} in ${e.binCode}. NetSuite still counts this unit until someone adjusts it there.`
        : null,
    reading: e.reading !== null && e.reading !== undefined ? `Meter ${e.reading.toLocaleString('en-US')}` : null,
  };
}

function groupMeta(g: PickHistoryGroupDto, shown: number): string {
  const total = g.entries.length;
  const isSubmit = g.kind === 'submit';
  const count =
    shown === total
      ? plural(total, isSubmit ? 'pick' : 'entry', isSubmit ? 'picks' : 'entries')
      : `${shown.toLocaleString('en-US')} of ${plural(total, isSubmit ? 'pick' : 'entry', isSubmit ? 'picks' : 'entries')}`;
  const when = `${phtFullDate(g.at)}, ${phtTimeWithSeconds(g.at)}`;
  return [isSubmit ? `submitted ${when}` : when, g.by, g.source, isSubmit ? null : g.reason, count]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

/**
 * The card body: groups by kind, rows by search, newest first as the server sent them; the counts on the kind buttons
 * are totals, never filtered. A search always shows every match; without one, a Submit over `CAP_OVER` rows shows its
 * latest `CAP_SHOW` until its id is in `expanded`.
 */
export function buildPickHistoryView(
  history: PickHistoryDto,
  q: string,
  kind: PickHistoryKindFilter,
  expanded: ReadonlySet<string>
): PickHistoryView {
  const query = q.trim();
  const counts: Record<PickHistoryKindFilter, number> = { all: 0, picks: 0, adjustments: 0 };
  for (const g of history.groups) {
    counts.all += g.entries.length;
    if (g.kind === 'submit') counts.picks += g.entries.length;
    else counts.adjustments += g.entries.length;
  }

  const groups: PickHistoryGroupView[] = [];
  let shown = 0;
  for (const g of history.groups) {
    if (kind === 'picks' && g.kind !== 'submit') continue;
    if (kind === 'adjustments' && g.kind !== 'adjust') continue;
    const matching = g.entries.filter((e) => entryMatches(e, query));
    if (matching.length === 0) continue;

    const isOpen = expanded.has(g.id);
    const capped = !query && matching.length > CAP_OVER;
    const visible = capped && !isOpen ? matching.slice(0, CAP_SHOW) : matching;
    const noun = g.kind === 'submit' ? 'picks in this submit' : 'entries in this adjustment';
    shown += visible.length;
    groups.push({
      id: g.id,
      kind: g.kind,
      label: g.kind === 'submit' ? 'Picked' : 'Adjusted',
      meta: groupMeta(g, matching.length),
      notes: g.kind === 'adjust' ? g.notes : null,
      rows: visible.map((e) => rowView(e, query)),
      moreText: capped
        ? isOpen
          ? `Showing all ${matching.length.toLocaleString('en-US')} ${noun}`
          : `Showing the latest ${CAP_SHOW} of ${matching.length.toLocaleString('en-US')} ${noun}`
        : null,
      moreLabel: capped ? (isOpen ? 'Show fewer' : `Show all ${matching.length.toLocaleString('en-US')}`) : null,
      expanded: isOpen,
    });
  }

  return {
    groups,
    counts,
    shown,
    total: counts.all,
    noMatch: counts.all > 0 && groups.length === 0,
    noMatchText: query ? `No entries match “${query}”` : 'No entries match this filter',
    clearLabel: query ? 'Clear search' : 'Clear filters',
  };
}

/** The collapsed card's one line: "9 entries · 7 picks · 2 adjustments · latest Oct 8, 10:39 AM". */
export function summaryLine(history: PickHistoryDto): string {
  let picks = 0;
  let adjustments = 0;
  for (const g of history.groups) {
    if (g.kind === 'submit') picks += g.entries.length;
    else adjustments += g.entries.length;
  }
  const total = picks + adjustments;
  if (total === 0) return 'No picks yet';
  const latest = history.groups[0]?.at;
  return [
    plural(total, 'entry', 'entries'),
    plural(picks, 'pick', 'picks'),
    plural(adjustments, 'adjustment', 'adjustments'),
    latest ? `latest ${phtMonthDay(latest)}, ${phtTimeOfDay(latest)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export interface PickHistoryGrid {
  columns: string;
  rowAreas: string;
  headAreas: string | null;
  gap: string;
  heads: { area: string; label: string; end: boolean }[];
  timeEnd: boolean;
  detailEnd: boolean;
}

/** README §1's three layouts, values verbatim. The narrow one has no column header. */
export function gridFor(layout: PickHistoryLayout): PickHistoryGrid {
  if (layout === 'wide') {
    return {
      columns: '92px minmax(0,2.4fr) minmax(0,1.4fr) minmax(0,1fr) minmax(0,1.1fr) 88px minmax(0,1.3fr)',
      rowAreas: '"time item mrs bin serial qty detail"',
      headAreas: '"time item mrs bin serial qty detail"',
      gap: '12px',
      heads: [
        { area: 'time', label: 'Date · time', end: false },
        { area: 'item', label: 'Item', end: false },
        { area: 'mrs', label: 'For MRS', end: false },
        { area: 'bin', label: 'Bin', end: false },
        { area: 'serial', label: 'Serial', end: false },
        { area: 'qty', label: 'Qty', end: true },
        { area: 'detail', label: 'Detail', end: false },
      ],
      timeEnd: false,
      detailEnd: false,
    };
  }
  if (layout === 'mid') {
    return {
      columns: '78px minmax(0,2fr) minmax(98px,1.35fr) minmax(0,1.15fr) 100px',
      rowAreas: '"time item mrs serial qty" "time item bin serial detail"',
      headAreas: '"time item mrs serial qty"',
      gap: '10px',
      heads: [
        { area: 'time', label: 'Date · time', end: false },
        { area: 'item', label: 'Item', end: false },
        { area: 'mrs', label: 'For MRS · Bin', end: false },
        { area: 'serial', label: 'Serial', end: false },
        { area: 'qty', label: 'Qty · Detail', end: true },
      ],
      timeEnd: false,
      detailEnd: true,
    };
  }
  return {
    columns: 'minmax(0,1fr) minmax(0,1fr) auto',
    rowAreas: '"item item qty" "mrs bin time" "serial serial detail"',
    headAreas: null,
    gap: '8px',
    heads: [],
    timeEnd: true,
    detailEnd: true,
  };
}
